import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';
import PlaceIcon from '@mui/icons-material/Place';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import {
    alpha,
    Box,
    darken,
    lighten,
    Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { FC, useEffect, useRef } from 'react';
import { CategorieBox } from '../../../../../core/CategorieBox';
import { FerrureBox } from '../../../../../core/FerrureBox';
import { OeillereBox } from '../../../../../core/OeillereBox';
import {
    capitalizeWords,
    formatDiscipline,
    formatHippodromeName,
    formatReduction,
    formatTypePiste,
    getCorde,
    getPlafondColor,
    getPoidHandicapLabel,
} from '../../../../../utils/format';
import { Corde, Discipline, TypeCourse } from '../../../services/enum';
import { Course, Prono, Report, Reunion } from '../../../services/type';
import useGetDetail from '../../../services/use-get-detail';
import CollapseDetail from './CollapseDetail';

interface Props {
    prono: Prono;
    report: Report;
    course: Course;
    reunion: Reunion;
    categorie?: string;
    selectedReport: Report | null;
    setSelectedReport: (report: Report | null) => void;
    otherHorses: Array<{ nom: string; num: number }>;
}

const ReportDetailCard: FC<Props> = ({
    report,
    prono,
    course,
    reunion,
    categorie,
    selectedReport,
    setSelectedReport,
    otherHorses,
}) => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const corde = getCorde(report.corde as Corde);
    const { data: details } = useGetDetail(selectedReport);
    const cardRef = useRef<HTMLDivElement | null>(null);

    // const ratio = getRatioGain(
    //     report.montantHorse / 100,
    //     report.plafond || undefined,
    //     report.recul || undefined
    // );

    const handleClick = () => {
        if (selectedReport === report) {
            setSelectedReport(null);
        } else {
            setSelectedReport(report);
        }
    };

    useEffect(() => {
        if (selectedReport === report && cardRef.current) {
            cardRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start', // ou "center" selon effet voulu
            });
        }
    }, [selectedReport]);

    // Header de la carte
    const header = (
        <Box
            width="15%"
            height="100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            alignSelf="center"
            borderRadius="8px 0px 0px 8px"
            {...(isMobile && { px: 0.5 })}
            sx={(theme) => ({
                background: `linear-gradient(
                    160deg,
                    ${lighten(theme.palette.primary.main, 0.15)} 0%,
                    ${theme.palette.primary.main} 55%,
                    ${darken(theme.palette.primary.main, 0.15)} 100%
                )`,
                boxShadow: `
                    inset -1px 0 0 rgba(255,255,255,0.15),
                    inset 0 1px 0 rgba(255,255,255,0.2),
                    2px 0 8px rgba(0,0,0,0.2)
                `,
            })}
        >
            <Typography
                fontWeight={700}
                fontSize={isMobile ? 16 : 20}
                color="white"
                lineHeight={1.15}
                sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.25)' }}
            >
                {`${report.date.slice(0, 2)}/${report.date.slice(2, 4)}`}
            </Typography>
            <Typography
                fontWeight={600}
                fontSize={13}
                color="secondary.light"
                lineHeight={1.15}
                sx={{ letterSpacing: 0.5 }}
            >
                {report.date.slice(4)}
            </Typography>
        </Box>
    );

    // Contenu principal de la carte
    const content = (
        <Box
            pl={isMobile ? 0.5 : 1}
            py={1}
            display="flex"
            flexDirection={'column'}
            color={'primary.main'}
            justifyContent={'space-evenly'}
            width={'70%'}
        >
            <Box
                display={'flex'}
                flexDirection={'row'}
                gap={isMobile ? 0.5 : 1}
                alignItems={'center'}
            >
                <PlaceIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                <Typography
                    sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isMobile ? '160px' : '300px',
                    }}
                    fontWeight={600}
                    variant="subtitle2"
                >
                    {formatHippodromeName(report.hypodromme)}
                </Typography>
                {corde.length > 0 && (
                    <Typography variant="subtitle2">{`(${corde})`}</Typography>
                )}
            </Box>
            <Box display={'flex'} flexDirection={'row'} gap={isMobile ? 0.5 : 1}>
                <SportsScoreIcon sx={{ color: 'secondary.main', fontSize: 20 }} />

                {report.categorie && (
                    <Typography
                        variant="subtitle2"
                        color={
                            !categorie
                                ? 'primary'
                                : report.categorie < categorie
                                    ? 'secondary.main'
                                    : report.categorie > categorie
                                        ? 'error.light'
                                        : 'primary'
                        }
                    >
                        {`cat: ${report.categorie}`}
                    </Typography>
                )}
                <Typography
                    variant="subtitle2"
                    color={
                        report.discipline !== course.discipline ? 'error.light' : 'primary'
                    }
                >
                    {formatDiscipline(report.discipline, isMobile)}
                </Typography>
                <Typography
                    variant="subtitle2"
                    color={
                        Math.abs(report.distance - course.distance) / course.distance > 0.15
                            ? 'error.light'
                            : Math.abs(report.distance - course.distance) / course.distance <=
                                0.05
                                ? 'secondary.main'
                                : 'primary'
                    }
                >{`${report.distance}m`}</Typography>
                {(report.handicapPoids > 0 || report.handicapValeur > 0) && (
                    <Typography variant="subtitle2">
                        {getPoidHandicapLabel(report.handicapValeur, report.handicapPoids)}
                    </Typography>
                )}
            </Box>
            <Box
                display={'flex'}
                flexDirection={'row'}
                gap={isMobile ? 0.5 : 1}
                alignItems={'center'}
            >
                <AttachMoneyIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                {report.discipline === Discipline.ATTELE ||
                    report.discipline === Discipline.MONTE ? (
                    <Box display="flex" alignItems="center" gap={1}>
                        {/* ===== MONTANT COURSE ===== */}
                        <Typography
                            variant="subtitle2"
                            color={
                                report.montantCourse > course.montantPrix * 1.2
                                    ? 'secondary'
                                    : report.montantCourse < course.montantPrix * 0.7
                                        ? 'error.light'
                                        : 'primary'
                            }
                        >
                            {`${report.montantCourse.toLocaleString('fr-FR')}€`}
                        </Typography>
                        {report.plafond != null && (
                            <Typography variant="subtitle2">/</Typography>
                        )}
                        {/* ===== PLAFOND ===== */}
                        {report.plafond != null && (
                            <>
                                <Typography variant="subtitle2">max:</Typography>
                                <Typography
                                    variant="subtitle2"
                                    color={getPlafondColor(report.plafond, course.plafond)}
                                    sx={{
                                        opacity: 0.85,
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {`${Math.round(report.plafond / 1000)}k`}
                                </Typography>
                            </>
                        )}
                        {report.groupType !== TypeCourse.INCONNU && (
                            <>
                                {report.groupType === TypeCourse.GROUPE_I && (
                                    <CategorieBox text={'Groupe I'}></CategorieBox>
                                )}
                                {report.groupType === TypeCourse.GROUPE_II && (
                                    <CategorieBox text={'Groupe II'}></CategorieBox>
                                )}
                                {report.groupType === TypeCourse.GROUPE_III && (
                                    <CategorieBox text={'Groupe III'}></CategorieBox>
                                )}
                            </>
                        )}
                    </Box>
                ) : (
                    <>
                        {report.type === TypeCourse.GROUPE_I && (
                            <CategorieBox text={'Groupe I'}></CategorieBox>
                        )}
                        {report.type === TypeCourse.GROUPE_II && (
                            <CategorieBox text={'Groupe II'}></CategorieBox>
                        )}
                        {report.type === TypeCourse.GROUPE_III && (
                            <CategorieBox text={'Groupe III'}></CategorieBox>
                        )}
                        <Typography
                            color={
                                report.montantCourse > course.montantPrix * 1.2
                                    ? 'secondary'
                                    : report.montantCourse < course.montantPrix * 0.7
                                        ? 'error.light'
                                        : 'primary'
                            }
                            variant="subtitle2"
                        >
                            {`${report.montantCourse.toLocaleString('fr-FR')}€`}
                        </Typography>
                        {report.typePiste && (
                            <Typography variant="subtitle2">{`${formatTypePiste(report.typePiste)}`}</Typography>
                        )}
                        {report.typePiste && report.penetrometre && (
                            <Typography variant="subtitle2">{`(${report.penetrometre})`}</Typography>
                        )}
                    </>
                )}
            </Box>
            <Box
                display={'flex'}
                flexDirection={'row'}
                gap={isMobile ? 0.5 : 1}
                alignItems={'center'}
            >
                <PersonIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                <Typography
                    sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isMobile ? '170px' : '300px',
                    }}
                    color={prono.data.jockey === report.jockey ? 'secondary' : 'primary'}
                    variant="subtitle2"
                >
                    {`${capitalizeWords(report.jockey)} / ${capitalizeWords(report.entraineur)}`}
                </Typography>
            </Box>
        </Box>
    );

    const getColorShadowReport = (theme: Theme): string => {
        if (report.jockey === prono.data.jockey) {
            if (['1', '2', '3'].includes(report.ordreArrivee)) {
                return theme.palette.secondary.main; // ✅ Utilisation correcte de la couleur MUI
            } else if (['4', '5', '6'].includes(report.ordreArrivee)) {
                return 'rgba(0, 0, 0, 0.31)'; // ✅ Directement utilisable dans CSS
            }
            return theme.palette.error.main; // ✅ Couleur d'erreur du thème
        }
        return 'rgba(0, 0, 0, 0.31)';
    };

    const footer = (
        <Box
            display="flex"
            flexDirection={'column'}
            justifyContent={'center'}
            alignItems={'flex-end'}
            width={'10%'}
            position={'relative'}
            gap={0.5}
            ref={cardRef}
        >
            <Box display="flex" flexDirection={'row'} minHeight={'20px'}>
                <FerrureBox
                    deferre={report.deferre}
                    isSecondary={report.deferre === prono.data.deferre}
                />
                <OeillereBox
                    oeillere={report.oeilleres}
                    isSecondary={report.oeilleres === prono.data.oeilleres}
                />
            </Box>
            <Box
                display="flex"
                color={'primary.main'}
                alignItems={'center'}
                justifyContent={'center'}
            >
                {report.ordreArrivee !== 'undefined' ? (
                    <>
                        {['1', '2', '3'].includes(report.ordreArrivee) && (
                            <EmojiEventsIcon
                                sx={{
                                    fontSize: 22,
                                    color: 'secondary.dark',
                                }}
                            />
                        )}
                        <Typography
                            fontWeight={600}
                            fontSize={24}
                            color={'primary.main'}
                        >{`${report.ordreArrivee}`}</Typography>
                    </>
                ) : (
                    <Typography fontWeight={600} color="text.disabled">
                        NP
                    </Typography>
                )}
            </Box>

            <Box
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                gap={0.5}
                minHeight={'20px'}
            >
                {report.reductionKilometrique && (
                    <>
                        <AccessTimeIcon
                            sx={{
                                fontSize: 16,

                            }}
                            color={
                                reunion.hippodrome.libelleLong === report.hypodromme
                                    ? 'secondary'
                                    : 'primary'
                            }
                        />
                        <Typography
                            variant="subtitle2"
                            fontWeight={500}
                            color={
                                reunion.hippodrome.libelleLong === report.hypodromme
                                    ? 'secondary'
                                    : 'primary'
                            }
                        >
                            {formatReduction(report.reductionKilometrique)}
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    );

    // const getResultBgColor = (theme: Theme): string => {
    //     if (report.ordreArrivee === 'undefined') return theme.palette.background.paper;
    //     const ordre = parseInt(report.ordreArrivee, 10);
    //     if (isNaN(ordre)) return theme.palette.background.paper;

    //     const sameJockeyPodium = report.jockey === prono.data.jockey && ordre <= 3;

    //     if (sameJockeyPodium) return alpha(theme.palette.success.main, 0.2); // renforcé
    //     if (ordre <= 3) return alpha(theme.palette.success.main, 0.15);
    //     if (ordre <= 5) return theme.palette.background.paper;
    //     return alpha(theme.palette.error.main, 0.15);
    // };

    const getResultBgColor = (theme: Theme): string => {
        if (report.ordreArrivee === 'undefined') return theme.palette.background.paper;
        const ordre = parseInt(report.ordreArrivee, 10);
        if (isNaN(ordre)) return theme.palette.background.paper;

        const sameJockeyPodium = report.jockey === prono.data.jockey && ordre <= 3;

        if (sameJockeyPodium) {
            return `linear-gradient(270deg, ${alpha(theme.palette.success.main, 0.22)} 0%, ${alpha(theme.palette.success.main, 0.14)} 85%, transparent 100%)`;
        }
        if (ordre <= 3) {
            return `linear-gradient(270deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.main, 0.10)} 85%, transparent 100%)`;
        }
        if (ordre <= 5) return theme.palette.background.paper;
        return `linear-gradient(270deg, ${alpha(theme.palette.error.main, 0.14)} 0%, ${alpha(theme.palette.error.main, 0.10)} 85%, transparent 100%)`;
    };

    return (
        <Box display={'flex'} flexDirection={'column'}>
            <Box
                onClick={handleClick}
                width="100%"
                height="100px"
                borderRadius={2}
                display="flex"
                flexDirection="row"
                overflow="hidden"
                sx={(theme) => ({
                    cursor: 'pointer',
                    // bgcolor: getResultBgColor(theme),
                    background: getResultBgColor(theme),
                    boxShadow: '0px 2px 6px rgba(0,0,0,0.15), 0px 6px 12px rgba(0,0,0,0.12)',
                    transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                    '&:hover': {
                        boxShadow: '0px 4px 10px rgba(0,0,0,0.18), 0px 12px 22px rgba(0,0,0,0.2)',
                        transform: 'translateY(-3px)',
                    },
                })}
            >
                {header}
                {content}
                {footer}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color={'text.disabled'}
                    width={isMobile ? '8%' : '6%'}
                    sx={{ transition: 'color 0.2s ease' }}
                >
                    <ExpandMoreIcon
                        sx={{
                            fontSize: 26,
                            transition: 'transform 0.25s ease',
                            transform:
                                report === selectedReport ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                </Box>
            </Box>
            <CollapseDetail
                isOpen={report === selectedReport}
                reports={details}
                currentReport={report}
                otherHorses={otherHorses}
            />
        </Box>
    );
};

export default ReportDetailCard;
